// Shared DTOs for the AI Agent / Credential / Agent Team admin UI.
//
// Field lists below were confirmed by directly reading flow-studio-designer's AgentEditor.tsx
// (2,347 lines) and flow-studio-api's credentialApiClient.ts/credential.types.ts, plus the real
// backend Team/TeamMember entities (BizFirst.Ai.AiAgent.Domain.Entities.Team/TeamMember). A
// handful of fields that exist on AgentFormData but have no UI control anywhere in the source
// editor (McpServerGroupID, McpClientID, and every *RoutingCustomRuleID field) are deliberately
// NOT carried over here — dead fields with no working control shouldn't be reintroduced in a
// fresh build. See DevelopmentHistoryLog.md for the full list of what was dropped and why.

import type { ReactNode } from 'react';

export interface AgentDTO {
  AgentID: number;
  ResID?: string;

  // Basic Info / Content
  Name: string;
  Description?: string;
  ShortDescription?: string;
  LongDescription?: string;
  IconUrl?: string;
  ImageUrl?: string;

  // Category
  AgentTypeID?: number | null;
  AgentCategoryID?: number | null;
  AgentClassificationID?: number | null;

  // LLM Configuration
  LlmProviderID?: number | null;
  LlmModelID?: number | null;
  LlmModelVersion?: string;
  Temperature?: number | null;
  MaxTokens?: number | null;
  LlmMaxOutputTokens?: number | null;
  LlmMaxRecursionDepth?: number | null;
  LlmReasoningEffortLevel?: string;
  LlmMaxMessageLimit?: number | null;

  // Advanced Settings
  PerformanceOptimizationIntensityID?: number | null;
  ResponseQualityIntensityID?: number | null;
  CompactMessageCount?: number | null;
  CompactTokenCount?: number | null;
  McpEnabled?: boolean;
  FunctionEnabled?: boolean;
  KnowledgeEnabled?: boolean;
  ConnectorID?: number | null;
  PrimaryConnectorGroupID?: number | null;
  ChannelGroupID?: number | null;
  IsRouter?: boolean;
  IsPlanner?: boolean;
  IsTask?: boolean;
  IsTaskUtility?: boolean;
  IsMergeUtility?: boolean;
  IsProcess?: boolean;
  IsProcessElement?: boolean;
  IsTemplate?: boolean;

  // Security
  MessageEncryptionEnforced?: boolean;
  OutgoingMessageDecryptionEnforced?: boolean;
  MessageSigningEnforced?: boolean;
  OutgoingMessageSigningEnforced?: boolean;
  IsPublic?: boolean;
  IsAnonymous?: boolean;
  IsGlobal?: boolean;
  Disabled?: boolean;
  Enabled?: boolean;
  Installed?: boolean;

  CreatedOn?: string;
  LastModifiedOn?: string;
}

export type AgentFormValues = Omit<AgentDTO, 'AgentID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

export interface InstructionDTO {
  InstructionID: number;
  AgentID: number;
  Name: string;
  Description?: string;
  Content: string;
  InstructionTypeID: number;
  ChannelID?: number | null;
  ProfileID?: number | null;
  IsActive: boolean;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type InstructionFormValues = Omit<InstructionDTO, 'InstructionID' | 'AgentID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

export interface LookupOption {
  id: number;
  name: string;
  [key: string]: unknown;
}

/** Confirmed real API endpoints backing each Agent-tab lookup dropdown (AgentEditor.tsx). */
export const AGENT_LOOKUP_ENDPOINTS = {
  // Lookups call the controllers' `active` search. The bare route is the create endpoint, and a
  // lookup POST to it returns 400.
  agentType: '/api/v1/ai-agent/agent-types/active',
  agentCategory: '/api/v1/ai-common/agent-categories/active',
  agentClassification: '/api/v1/ai-common/agent-classifications/active',
  // Provider has no list/active route, only `enabled`, which returns the enabled providers.
  llmProvider: '/api/v1/ai-llm/providers/enabled',
  llmModel: '/api/v1/ai-llm/models/active',
  performanceIntensity: '/api/v1/ai-common/performance-optimization-intensities/active',
  qualityIntensity: '/api/v1/ai-common/response-quality-intensities/active',
  connector: '/api/v1/ai-extension/connectors/active',
  connectorGroup: '/api/v1/ai-extension/connector-groups/active',
  channelGroup: '/api/v1/ai-channel/channel-groups/active',
} as const;

export type AgentLookupKey = keyof typeof AGENT_LOOKUP_ENDPOINTS;

// ---------------------------------------------------------------------------------------------
// Credential
// ---------------------------------------------------------------------------------------------

export interface CredentialDTO {
  CredentialID: number;
  Name: string;
  Description?: string;
  CredentialTypeID: number;
  DisplayMetadata?: string;
  ExecutionScopeID?: number;
  ScopeEntityID?: string;
  EnvironmentID?: number | null;
  IsShared?: boolean;
  ExpiresAt?: string;
  LastUsedAt?: string;
  UsageCount?: number;
  Enabled?: boolean;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export interface CredentialTypeDTO {
  CredentialTypeID: number;
  Code: string;
  Name: string;
  Description?: string;
  IconUrl?: string;
  IsOAuth?: boolean;
  RequiresRefresh?: boolean;
  Enabled?: boolean;
  DisplayOrder?: number;
  /** Real backend column (AIExt_CredentialTypes.FieldsSchema) — JSON, e.g. `{ fallbackFormCode }`.
   * Not consumed by the default plain-HTML renderer; carried here only so a custom
   * CredentialFormRenderer (e.g. Flow Studio's Atlas Forms adapter) can read step-3 of its own
   * form-resolution strategy without a separate fetch. */
  FieldsSchema?: string;
}

/** A credential picker/section restricted to specific types (mirrors flow-studio's AcceptedCredentialType). */
export interface AcceptedCredentialType {
  code: string;
  editFormID?: number;
  viewFormID?: number;
}

export interface CredentialFormControl {
  id: string;
  type: 'text' | 'password' | 'textarea' | 'select' | 'checkbox' | 'number' | string;
  label: string;
  placeholder?: string;
  required?: boolean;
  helpText?: string;
  options?: Array<{ label: string; value: string }>;
}

export interface CredentialFormSchema {
  version?: string;
  metadata?: {
    formID?: number;
    title?: string;
    description?: string;
    credentialTypeCode?: string;
  };
  controls: CredentialFormControl[];
}

export interface CredentialFormSchemaResponse {
  schema: CredentialFormSchema;
}

/**
 * Decrypted field bag used only when editing an existing credential (fetched over HTTPS, used
 * sparingly). IMPORTANT: the backend's `/credentials/get-by-id/decrypted` endpoint
 * (CredentialEncryptionService.GetDecryptedCredentialDataAsync) returns the vault's decrypted
 * payload as a bare JSON string — `GoWebResponse<string>` — NOT an object with
 * CredentialID/Name/CredentialTypeID/data fields. This type describes the field bag AFTER
 * `credentialAdminApiClient.getByIdDecrypted()` has JSON.parsed that string.
 *
 * 2026-09-14 incident: CredentialEditor.tsx used to read `decrypted.data.data`, which — since
 * `decrypted.data` was already the raw string — always evaluated to `undefined` and silently
 * fell back to `{}`. That made every type-specific field (API Key, API Secret, Base URL, …)
 * render empty on edit, and because Atlas Forms reports the *whole* form state on every field
 * change, a subsequent Save wrote those blanks back and overwrote the real encrypted secrets.
 * Keep this type and getByIdDecrypted's JSON.parse in sync or that bug reappears.
 */
export type DecryptedCredentialData = Record<string, unknown>;

// ---------------------------------------------------------------------------------------------
// Credential form-rendering extension points (pluggable dynamic form + OAuth2 connect)
//
// Restores flow-studio-designer's original Atlas-Forms-driven dynamic per-credential-type form
// and OAuth2 popup connect flow (deleted 2026-08-19/20, commit b17411167, from CredentialDialog.tsx/
// CredentialSection.tsx/CredentialSelector.tsx) as an *optional* extension point rather than a
// hard dependency — CredentialEditor.tsx defaults to today's plain-HTML rendering when a
// consuming app supplies neither. Both are resolved and rendered as JSX components
// (`<FormRenderer .../>`), never called as bare functions — required so a hooks-based custom
// renderer (e.g. one wrapping Atlas Forms' own FormRenderer) never breaks React's rules of hooks
// under conditional rendering.
// ---------------------------------------------------------------------------------------------

/** Props passed to a pluggable dynamic credential-fields renderer. */
export interface CredentialFormRendererProps {
  schema: CredentialFormSchema;
  credentialType: CredentialTypeDTO;
  /** The CredentialPicker's matching AcceptedCredentialType entry for this credential type, when
   * known (carries editFormID/viewFormID for a custom renderer's own form-resolution logic, e.g.
   * Flow Studio's Atlas Forms adapter's step-1 override). Undefined for a search-all/type-agnostic
   * picker or a standalone create/edit flow with no accepted-types list. */
  acceptedType?: AcceptedCredentialType;
  mode: 'create' | 'edit';
  values: Record<string, unknown>;
  onFieldChange: (fieldId: string, value: unknown) => void;
}

/** A renderer is just a component function — no Atlas Forms types appear in this file, so
 * importing this type never pulls Atlas Forms into a consumer that doesn't pass one. */
export type CredentialFormRenderer = (props: CredentialFormRendererProps) => ReactNode;

// OAuth is a second, independent extension point — a credential type flagged IsOAuth can
// optionally get a "Connect" control; consumers that don't supply one keep today's plain-text
// note-and-paste behavior exactly as it is now.
export interface CredentialOAuthConnectProps {
  credentialType: CredentialTypeDTO;
  formValues: Record<string, unknown>;
  /** Renderer calls this once the popup/redirect flow yields an exchanged code; the host
   *  decides what field(s) the result populates. */
  onExchanged: (fieldUpdates: Record<string, unknown>) => void;
}

export type CredentialOAuthConnect = (props: CredentialOAuthConnectProps) => ReactNode;

// ---------------------------------------------------------------------------------------------
// Agent Team
// ---------------------------------------------------------------------------------------------

export interface TeamDTO {
  TeamID: number;
  TeamName: string;
  Description?: string;
  TeamTypeID: number;
  Purpose?: string;
  IsActive: boolean;
  LeaderAgentID?: number | null;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type TeamFormValues = Omit<TeamDTO, 'TeamID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

export interface TeamMemberDTO {
  TeamMemberID: number;
  TeamID: number;
  AgentID: number;
  Role?: string;
  Capabilities?: string;
  JoinedOn?: string;
  LeftOn?: string | null;
  IsActive: boolean;
}

export interface PagedResult<T> {
  items: T[];
  totalCount?: number;
}

// ---------------------------------------------------------------------------------------------
// Agent Template (Phase 2 — restored sub-entity tab)
//
// Real backend entity (BizFirst.Ai.AiAgent.Domain.Entities.AgentTemplate, table
// AIAgent_AgentTemplates), confirmed by reading the entity directly: AgentID/Name/Content/
// AgentTemplateFormatID/AgentTemplateTypeID — NOTHING else. In particular there is NO
// `Description` property on the entity at all: BaseAgentTemplateController's dedicated
// CreateTemplateRequest/UpdateTemplateRequest DTOs (the "New Write Operations" region, routes
// create-template/update-template) do accept a `Description` string, but AgentTemplateService
// never assigns it onto the entity before persisting — a dead, unpersisted request field. This
// client deliberately uses the *generic* CRUD routes (POST/PUT/DELETE on the base
// `agent-templates` route, bound to InsertWebRequest/UpdateWebRequest against the entity's real
// fields) rather than the dedicated create-template/update-template/delete-template routes, so
// there's no Description-shaped field to accidentally wire up as a no-op.
// ---------------------------------------------------------------------------------------------

export interface AgentTemplateDTO {
  AgentTemplateID: number;
  AgentID: number;
  Name: string;
  Content: string;
  AgentTemplateFormatID?: number | null;
  AgentTemplateTypeID?: number | null;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type AgentTemplateFormValues = Omit<AgentTemplateDTO, 'AgentTemplateID' | 'AgentID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

// ---------------------------------------------------------------------------------------------
// Agent Rule (Phase 2 — restored sub-entity tab)
//
// Real backend entity (BizFirst.Ai.AiAgent.Domain.Entities.Rule, table AIAgent_Rules), confirmed
// by reading the entity directly: RuleName/RuleDescription/Enabled/AgentRuleTypeID/
// TriggerEventID/AppliedOnAttribute1-9 — NOT RulePriority/RuleExpression/IsActive (both the old
// deleted frontend and the design doc assumed those three, which don't exist on the entity).
// ---------------------------------------------------------------------------------------------

export interface AgentRuleDTO {
  AgentRuleID: number;
  AgentID: number;
  AgentRuleTypeID?: number | null;
  TriggerEventID?: number | null;
  RuleName: string;
  RuleDescription?: string;
  AppliedOnAttribute1?: string;
  AppliedOnAttribute2?: string;
  AppliedOnAttribute3?: string;
  AppliedOnAttribute4?: string;
  AppliedOnAttribute5?: string;
  AppliedOnAttribute6?: string;
  AppliedOnAttribute7?: string;
  AppliedOnAttribute8?: string;
  AppliedOnAttribute9?: string;
  Enabled: boolean;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type AgentRuleFormValues = Omit<AgentRuleDTO, 'AgentRuleID' | 'AgentID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

// ---------------------------------------------------------------------------------------------
// Agent Hook (Phase 2 — restored sub-entity tab)
//
// AIAgent_AgentHooks is a plain junction row (BizFirst.Ai.AiAgent.Domain.Entities.AgentHook) —
// AgentID + HookID + IsActive, plus three agent-memory-hook-specific override columns
// (ScopeMode/OwnerScope/ConfigJson, confirmed real but out of scope for this v1 panel — no UI
// control for ConfigJson's raw JSON here, matching InstructionsPanel's own precedent of not
// building a control for every advanced column). No link/unlink API exists on
// BaseAgentHookController.cs — confirmed by reading it directly, it's the same generic Create/
// Update/Delete/GetAll/GetById/GetByAgent(`by-agent`)/GetActive/Activate/Deactivate/Validate
// shape every other sub-entity controller here has. "Link"/"unlink" is therefore implemented as
// Create/Delete on this junction row, exactly as the task's own note prescribes.
//
// HookDTO (below) is the separate *hook definition* row (BizFirst.Ai.AIExtension.Domain.
// Entities.Hook, table AIExt_Hooks, controller BaseHookController @ /api/v1/ai-extension/hooks)
// that AgentHookDTO.HookID points at — what the "Add Hook" picker lists.
// ---------------------------------------------------------------------------------------------

export interface AgentHookDTO {
  AgentHookID: number;
  AgentID: number;
  HookID: number;
  IsActive: boolean;
  ScopeMode?: string | null;
  OwnerScope?: string | null;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type AgentHookFormValues = Omit<AgentHookDTO, 'AgentHookID' | 'AgentID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

/** The Hook *definition* row (AIExt_Hooks) — distinct from AgentHookDTO, the per-agent link row. */
export interface HookDTO {
  HookID: number;
  Name: string;
  Description?: string;
  HookTypeID?: number | null;
}

// ---------------------------------------------------------------------------------------------
// Phase 3 — the remaining 7 restored sub-entity tabs (Capabilities/Quotas/Policies/Payment
// Rules/Data Samples/Scopes/Versions). Unlike Phase 2's three (Templates/Rules/Hooks), none of
// these seven have confirmed real production seed data — they carry a visible "Unverified" label
// in AgentEditor.tsx (see UnverifiedBanner.tsx and the `tier` field on SUB_ENTITY_TABS) even
// though their field/route shapes below were, like Phase 2's, verified directly against the live
// BizFirstPayrollV3 backend rather than assumed from the task briefing or the design doc. See
// DevelopmentHistoryLog.md for the full per-tab audit, including drift found beyond the
// briefing's own hypotheses.
// ---------------------------------------------------------------------------------------------

// --- Capability (AIAgent_Capabilities) ---
//
// Real entity (BizFirst.Ai.AiAgent.Domain.Entities.Capability) confirmed by reading it directly:
// Description (not CapabilityDescription), a required CapabilityTypeID FK, Parameters/
// Prerequisites (JSON strings), Cost (decimal), IsAdvertised (bool) — no IsEnabled/
// CapabilityVersion, neither of which exists. NOT mentioned by the task briefing at all but real
// and required: CapabilityName (string, 200) — the entity's actual display-name column.

export interface CapabilityDTO {
  CapabilityID: number;
  AgentID: number;
  CapabilityName: string;
  Description?: string;
  CapabilityTypeID: number;
  Parameters?: string;
  Prerequisites?: string;
  Cost?: number | null;
  IsAdvertised: boolean;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type CapabilityFormValues = Omit<CapabilityDTO, 'CapabilityID' | 'AgentID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

// --- AgentQuota (AIAgent_AgentQuotas) ---
//
// Real entity confirmed by reading AgentQuota.cs directly: just AgentID + a required QuotaTypeID
// FK + a required decimal Value — no QuotaName/QuotaPeriod enum/CurrentUsage rollup, exactly as
// the briefing hypothesized. Route drift found instead: see agentQuotaAdminApiClient.ts.

export interface AgentQuotaDTO {
  AgentQuotaID: number;
  AgentID: number;
  QuotaTypeID: number;
  Value: number;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type AgentQuotaFormValues = Omit<AgentQuotaDTO, 'AgentQuotaID' | 'AgentID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

// --- Policy (AIAgent_Policies) ---
//
// Real entity (BizFirst.Ai.AiAgent.Domain.Entities.Policy) confirmed by reading it directly: no
// PolicyContent/IsActive; a required ResourceTypeID FK; six required AppliedOnAttribute1-6
// string(300) columns — all as briefed. NOT mentioned by the briefing but real and `[Required]`
// on the entity: PolicyTypeID (int FK), PolicyName (string, 300), PolicyDescription (string,
// 2000). All three must be included in every create/update payload.

export interface AgentPolicyDTO {
  AgentPolicyID: number;
  AgentID: number;
  PolicyTypeID: number;
  ResourceTypeID: number;
  PolicyName: string;
  PolicyDescription: string;
  AppliedOnAttribute1: string;
  AppliedOnAttribute2: string;
  AppliedOnAttribute3: string;
  AppliedOnAttribute4: string;
  AppliedOnAttribute5: string;
  AppliedOnAttribute6: string;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type AgentPolicyFormValues = Omit<AgentPolicyDTO, 'AgentPolicyID' | 'AgentID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

// --- PaymentRule (AIAgent_PaymentRules) ---
//
// Real entity (BizFirst.Ai.AiAgent.Domain.Entities.PaymentRule) confirmed by reading it directly:
// a blockchain payment-validation record — PaymentAccountID/Amount/Currency/MaxSpendLimit/
// IsValidateSenderSignature/IsValidateRecipientSignature/Attribute1-9 — not a percentage-based
// rule, exactly as briefed. NOT mentioned by the briefing: optional EventID/RequestID/
// ConversationID/AgentRunID int FKs and an optional Hash string(200) column. See
// agentPaymentRuleAdminApiClient.ts for the confirmed missing-by-agent-route gap and its
// client-side-filter workaround.

export interface PaymentRuleDTO {
  PaymentRuleID: number;
  AgentID: number;
  PaymentAccountID: number;
  EventID?: number | null;
  RequestID?: number | null;
  ConversationID?: number | null;
  AgentRunID?: number | null;
  IsValidateSenderSignature: boolean;
  IsValidateRecipientSignature: boolean;
  Attribute1?: string;
  Attribute2?: string;
  Attribute3?: string;
  Attribute4?: string;
  Attribute5?: string;
  Attribute6?: string;
  Attribute7?: string;
  Attribute8?: string;
  Attribute9?: string;
  Amount: number;
  Currency: string;
  MaxSpendLimit?: number | null;
  Hash?: string;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type PaymentRuleFormValues = Omit<PaymentRuleDTO, 'PaymentRuleID' | 'AgentID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

// --- AgentDataSample (AIAgent_AgentDataSamples) ---
//
// Real entity confirmed by reading AgentDataSample.cs directly: Name/Description/Content (one
// free-form field)/IsActive — no input/output split, exactly as briefed. See
// agentDataSampleAdminApiClient.ts for the confirmed POST update/POST delete route-shape quirk.

export interface AgentDataSampleDTO {
  AgentDataSampleID: number;
  AgentID: number;
  Name: string;
  Description?: string;
  Content: string;
  IsActive: boolean;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type AgentDataSampleFormValues = Omit<AgentDataSampleDTO, 'AgentDataSampleID' | 'AgentID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

// --- AgentScope (AIAgent_Scopes) ---
//
// Real entity confirmed by reading AgentScope.cs directly: a bare junction row — AgentID + a
// required ScopeTypeID FK, nothing else — exactly as briefed. No ScopeName/string ScopeType/
// ScopeDescription/ScopePermissions/IsActive.

export interface AgentScopeDTO {
  AgentScopeID: number;
  AgentID: number;
  ScopeTypeID: number;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type AgentScopeFormValues = Omit<AgentScopeDTO, 'AgentScopeID' | 'AgentID' | 'ResID' | 'CreatedOn' | 'LastModifiedOn'>;

// --- Version (AIAgent_Versions) — read-only tab, no FormValues type ---
//
// Real entity (BizFirst.Ai.AiAgent.Domain.Entities.Version) confirmed richer than briefed:
// ConfigSnapshot/InstructionSnapshot (JSON)/IsActive/DeployedOn/DeployedBy/UndeployedOn/
// PerformanceMetrics (JSON), description field is `Description` not `VersionDescription` — all as
// briefed. NOT mentioned by the briefing but real and required: VersionNumber (string, 50).

export interface AgentVersionDTO {
  VersionID: number;
  AgentID: number;
  VersionNumber: string;
  Description?: string;
  ConfigSnapshot?: string;
  InstructionSnapshot?: string;
  IsActive: boolean;
  DeployedOn?: string | null;
  DeployedBy?: number | null;
  UndeployedOn?: string | null;
  PerformanceMetrics?: string;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

// ---------------------------------------------------------------------------------------------
// MCP Server (AiMcp module: BizFirst.Ai.AiMcp.Domain.Entities.McpServer, table AIMCP_McpServers).
// Routes: api/v1/ai-mcp/mcp-servers. Required on the entity: Name (200) and McpServerTypeID.
// ConfigJson/ConnectionString/credential references are not exposed in the v1 editor.
// ---------------------------------------------------------------------------------------------

export interface McpServerDTO {
  McpServerID: number;
  Name: string;
  Description?: string;
  ShortDescription?: string;
  LongDescription?: string;
  IconUrl?: string;
  ImageUrl?: string;
  HelpDocumentation?: string;
  McpServerTypeID: number;
  McpServerCategoryID?: number | null;
  McpServerGroupID?: number | null;
  IsActive: boolean;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type McpServerFormValues = Omit<McpServerDTO, 'McpServerID' | 'McpServerGroupID' | 'CreatedOn' | 'LastModifiedOn' | 'ResID'>;

// ---------------------------------------------------------------------------------------------
// MCP Server Group (AIMCP_McpServerGroups) and its membership junction (AIMCP_McpServerGroupMembers).
// The backend group has NO name column: a group is identified by McpServerGroupID, with a primary
// McpServerID and a ServerRole. Membership lives in McpServerGroupMember rows (group + server +
// DisplayOrder). Routes: api/v1/ai-mcp/mcp-server-groups and .../mcp-server-group-members.
// ---------------------------------------------------------------------------------------------

export interface McpServerGroupDTO {
  McpServerGroupID: number;
  McpServerID: number;
  ServerRole: string;
  IsActive: boolean;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type McpServerGroupFormValues = Omit<McpServerGroupDTO, 'McpServerGroupID' | 'CreatedOn' | 'LastModifiedOn' | 'ResID'>;

export interface McpServerGroupMemberDTO {
  McpServerGroupMemberID: number;
  McpServerGroupID: number;
  McpServerID: number;
  DisplayOrder: number;
  IsActive: boolean;
}

// ---------------------------------------------------------------------------------------------
// Agent Function (AIFunction module: BizFirst.Ai.AIFunction.Domain.Entities.AgentFunction, table
// AIFunction_AgentFunctions). Routes: api/v1/ai-function/agent-functions. Required on the entity:
// AgentID and Name (200). The entity has no IsActive flag. Parameters is a JSON schema string; the
// child Parameter/ParameterValidation tables and credential references are not in the v1 editor.
// ---------------------------------------------------------------------------------------------

export interface AgentFunctionDTO {
  AgentFunctionID: number;
  AgentID: number;
  Name: string;
  Description?: string;
  Parameters?: string;
  FunctionTypeID?: number | null;
  McpToolID?: number | null;
  Assembly?: string;
  ClassName?: string;
  MethodName?: string;
  CreatedOn?: string;
  LastModifiedOn?: string;
  ResID?: string;
}

export type AgentFunctionFormValues = Omit<AgentFunctionDTO, 'AgentFunctionID' | 'CreatedOn' | 'LastModifiedOn' | 'ResID'>;
